import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { CaretIcon } from '@rsos/assets/icons';
import { Box, Select, Text } from '@rsos/components/capstone/base';
import { ChevronContainer } from '@rsos/components/capstone/base/Select/Select.styles';
import { trackFloorSelector } from '@rsos/components/capstone/common/Map/SpatialLayers/spatialLayersTrackings';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import { createVectorTileLayer } from '@rsos/maps/providers/esri/layers/createVectorTileLayer';
import { EsriMapsContext } from '../../../JurisdictionView/Map/EsriMapsProvider';
import {
  GEOCOMM_INDOOR_MAPS,
  useMinAllowableIndex,
} from '../../../JurisdictionView/Map/GeoCommIndoorMaps';
import { FLOOR_SELECTOR } from './constants';

// Portal the select menu to a DOM element higher up in the component tree so
// it's not hidden by the card's overflow-y
const PORTAL_TARGET = document.querySelector('body');

const DropdownIndicator = props => {
  return (
    <ChevronContainer
      style={{
        ...(props.selectProps.isLoading ? { display: 'none' } : {}),
        ...(props.isDisabled ? { cursor: 'not-allowed' } : {}),
        transform: `rotate(${props.selectProps.menuIsOpen ? -180 : 0}deg)`,
      }}
      data-name="select-arrow"
    >
      <CaretIcon width={7} height={11} />
    </ChevronContainer>
  );
};

const customComponents = {
  DropdownIndicator: DropdownIndicator,
};

/**
 * Custom floor selector component for controlling Geocomm vector tile layer
 * data in Premium
 * @param {Object} props - Ellington props
 */
const FloorSelector = withTheme(props => {
  const floorData = props.data[FLOOR_SELECTOR];
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [filteredLayers, setFilteredLayers] = useState([]);
  const [isVectorTileLoading, setIsVectorTileLoading] = useState(false);
  const [vectorTileHasError, setVectorTileHasError] = useState(false);

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  // Note: the context does not update when adding new layers, so the mapLayers
  // value will be stale when adding new layers on the fly.
  const map = useContext(EsriMapsContext);
  const mapLayers = map?.mapInstance?.map?.layers;

  const irpVersion = useSelector(state => state.sinatra.irp.irpVersion);

  const layerData = useSelector(state => state.mapCard.mapCardData.layerData);
  const spatialObject = useSelector(
    state => state.mapCard.mapCardData.spatialObject,
  );

  const theme = props.theme;

  const isDisabled = useMemo(() => {
    return floorData.floorDetails.length < 1;
  }, [floorData]);

  // There should always be floor data, otherwise this component shouldn't render
  const floorOptions = useMemo(() => {
    return floorData.floorDetails.map(floor => {
      return {
        value: `${floor.floorIndex}`,
        label: `Floor Level: ${floor.floorLabel}`,
        isSelected: floor.floorIndex === selectedFloor?.floorIndex,
        data: floor,
      };
    });
  }, [floorData, selectedFloor]);

  const selectedFloorIndex = floorOptions.findIndex(floor => {
    return parseInt(floor?.value) === parseInt(selectedFloor?.floorIndex);
  });

  const minAllowableIndex = useMinAllowableIndex();

  const selectStyles = {
    clearIndicator: styles => ({
      ...styles,
      display: 'none', // hide the `x` icon since search is disabled for this phase
    }),
    container: styles => {
      return {
        color: theme.colors.primaryText,
        ...styles,
        height: 25,
        pointerEvents: 'auto',
        width: '100%',
        zIndex: 555,
      };
    },
    control: styles => ({
      ...styles,
      borderColor: theme.colors.heavyLineOutline,
      cursor: isDisabled || selectedFloor === null ? 'not-allowed' : 'pointer',
      maxHeight: 25,
      minHeight: 20,
      width: '100%',
    }),
    indicatorsContainer: styles => ({
      ...styles,
      cursor: 'inherit',
      height: 25,
      width: 30,
    }),
    menu: styles => ({
      ...styles,
      cursor: 'pointer',
    }),
    menuList: styles => ({
      ...styles,
      cursor: 'pointer',
    }),
    option: styles => ({
      ...styles,
      background: theme.colors.background,
      color: theme.colors.primaryText,
      cursor: 'pointer',
      fontSize: '14px',
    }),
    valueContainer: styles => ({
      ...styles,
      height: 25,
    }),
  };

  /**
   * Handle changing the visibility of the corresponding vector tile layer.
   * Handle the selected floor in the Select component
   * If the vector tile layer exists, update its visibility, otherwise create it
   * @param {Object} data - floorOptions.data
   */
  const handleOptionChange = async option => {
    setIsVectorTileLoading(true);
    setVectorTileHasError(false);
    const prevLayer = filteredLayers.find(layer => layer.visible);
    if (prevLayer) {
      prevLayer.visible = false;
    }

    const selectedLayer = filteredLayers.find(
      layer => layer.url === option.data.vectorTileUrl,
    );
    if (selectedLayer) {
      selectedLayer.visible = true;
      const selectedFloor = floorData.floorDetails.find(
        floor => floor.vectorTileUrl === selectedLayer.url,
      );
      setSelectedFloor(selectedFloor);
      setIsVectorTileLoading(false);
    } else {
      const nextFloor = option.data;
      const floorSelectorData = spatialObject.attributes[FLOOR_SELECTOR];
      try {
        const vectorTileLayer = await createVectorTileLayer({
          id: `geocomm-indoor-maps__${spatialObject.id}__${nextFloor.vectorTileUrl}`,
          url: nextFloor.vectorTileUrl,
          visible: true,
          title: floorSelectorData?.building?.siteName,
        });

        map.mapInstance.map.add(vectorTileLayer, minAllowableIndex);
        // Append to front of list for the .find() to be quicker
        setFilteredLayers([vectorTileLayer, ...filteredLayers]);

        const selectedFloor = floorData.floorDetails.find(
          floor => floor.vectorTileUrl === nextFloor.vectorTileUrl,
        );
        setSelectedFloor(selectedFloor);
      } catch {
        setVectorTileHasError(true);
      } finally {
        setIsVectorTileLoading(false);
      }
    }
  };

  const handleSelectChange = option => {
    trackFloorSelector(irpVersion, layerData.source);
    handleOptionChange(option);
  };

  const handleFilterLayers = useCallback(() => {
    // Filter out the layers with ids that don't partial match
    // `geocomm_indoor-maps__${spatialObject.id}`
    if (mapLayers?.items?.length) {
      const layers = mapLayers.items.filter(layer => {
        return layer.id.includes(
          `${GEOCOMM_INDOOR_MAPS}__${spatialObject?.id}`,
        );
      });
      setFilteredLayers(layers);
    }
  }, [mapLayers, spatialObject?.id]);

  useEffect(() => {
    handleFilterLayers();
  }, [handleFilterLayers, mapLayers, spatialObject?.id]);

  useEffect(() => {
    // Update the selected floor based on the vector tile layer's visibility.
    // This should only happen once on mount
    if (filteredLayers.length) {
      const visibleLayer = filteredLayers.find(layer => layer?.visible);
      const visibleFloor = floorData.floorDetails.find(
        floor => floor.vectorTileUrl === visibleLayer?.url,
      );
      if (
        !selectedFloor ||
        selectedFloor?.vectorTileUrl !== visibleFloor?.vectorTileUrl
      ) {
        setSelectedFloor(visibleFloor);
      }
    } else {
      setSelectedFloor({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLayers]);

  return mapProvider === GOOGLE ? (
    <Box paddingX="13px">
      <Text color="primaryText" size="small">
        More detailed Geocomm Indoor Map data available with Esri Maps.
      </Text>
    </Box>
  ) : (
    <Box px="13px" pt="9px">
      {vectorTileHasError ? (
        <Text color="lightDanger" mb="9px">
          Indoor Maps Failed To Load. Contact Support
        </Text>
      ) : floorOptions.length ? (
        <Text color="secondaryText" mb="9px">
          {selectedFloorIndex >= 0
            ? `Floor ${selectedFloorIndex + 1} of ${
                floorOptions.length
              } selected`
            : 'No floor selected'}
        </Text>
      ) : (
        <Text color="secondaryText" mb="9px">
          No Indoor Map Data Available
        </Text>
      )}

      <Select
        captureMenuScroll={true}
        components={customComponents}
        isDisabled={isDisabled || selectedFloor === null}
        isLoading={selectedFloor === null || isVectorTileLoading}
        isSearchable={false}
        menuPortalTarget={PORTAL_TARGET}
        onChange={handleSelectChange}
        options={floorOptions}
        placeholder={
          <Text color="secondaryText" size="small">
            {!floorOptions.length
              ? 'No floors available'
              : selectedFloorIndex === -1
              ? 'Select a floor'
              : ''}
            {/* The empty string should never show up when there is a selected value */}
          </Text>
        }
        styles={selectStyles}
        theme={theme}
        value={floorOptions[selectedFloorIndex]}
      />
    </Box>
  );
});

export default FloorSelector;
